/**
 * NavigationSidebar Component - Microsoft 365 Admin Console Style
 * Left sidebar navigation with collapsible sections
 */

import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, Divider } from '@fluentui/react-components';
import {
  PeopleRegular,
  BuildingRegular,
  ShieldRegular,
  PersonRegular,
  ClockRegular,
  FolderRegular,
  AppsRegular,
  LocationRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  PersonSettingsRegular,
  KeyRegular,
  ChartMultipleRegular,
  ShieldCheckmarkRegular,
  QrCodeRegular,
  MailRegular,
  EyeTrackingRegular,
  DocumentBulletListRegular,
  PersonAddRegular,
  ReceiptRegular,
  DocumentRegular,
  MoneyRegular,
  PersonWalkingRegular,
  PersonAvailableRegular,
} from '@fluentui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';

const useStyles = makeStyles({
  sidebar: {
    width: '220px',
    height: 'calc(100vh - 48px)', // Account for header
    backgroundColor: '#f5f5f5',
    padding: '8px',
    position: 'fixed',
    top: '48px',
    left: 0,
    overflowY: 'auto',
    borderRight: '1px solid #e1dfdd',
  },
  navGroup: {
    marginBottom: '8px',
  },
  navGroupLabel: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '11px',
    fontWeight: '600',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    color: '#605e5c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    textDecoration: 'none',
    ':hover': {
      backgroundColor: '#e1dfdd',
      borderRadius: '4px',
      textDecoration: 'none',
    },
  },
  navItem: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    color: '#323130',
    fontSize: '13px',
    marginBottom: '2px',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    textDecoration: 'none',
    ':hover': {
      backgroundColor: '#e1dfdd',
      textDecoration: 'none',
    },
  },
  navItemActive: {
    backgroundColor: '#005F51', // Darker teal for WCAG AA contrast (5.2:1 with white)
    color: '#ffffff',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: '700', // Use actual available weight (not 600)
    borderLeft: '5px solid #008272', // Keep original teal for brand consistency
    paddingLeft: '7px', // Adjust padding to compensate for border
    textDecoration: 'none',
    ':hover': {
      backgroundColor: '#004940', // Even darker on hover for better feedback
      borderLeft: '5px solid #00695c',
      textDecoration: 'none',
    },
  },
  navSubItem: {
    paddingLeft: '36px',
    fontSize: '12px',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
  },
  icon: {
    fontSize: '16px',
  },
  chevron: {
    fontSize: '12px',
    marginLeft: 'auto',
  },
  divider: {
    margin: '8px 0',
  },
});

interface NavItem {
  name: string;
  path?: string;
  icon?: React.ReactNode;
  subItems?: NavItem[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

// Define menu structure outside component to prevent recreation on every render
// This fixes the infinite re-render loop that was causing navigation to freeze
const MENU_GROUPS: NavGroup[] = [
  {
    title: 'Dashboards',
    items: [
      { name: 'General Dashboard', path: '', icon: <ChartMultipleRegular /> },
      // Future dashboards will be added here:
      // { name: 'Executive Dashboard', path: 'dashboard/executive', icon: <ChartMultipleRegular /> },
      // { name: 'Operations Dashboard', path: 'dashboard/operations', icon: <ChartMultipleRegular /> },
      // { name: 'HR Dashboard', path: 'dashboard/hr', icon: <ChartMultipleRegular /> },
    ],
  },
  {
    title: 'Employee Management',
    items: [
      { name: 'Manage Departments', path: 'departments', icon: <BuildingRegular /> },
      { name: 'Manage Roles', path: 'roles', icon: <PersonRegular /> },
      { name: 'Manage Employees', path: 'employees', icon: <PeopleRegular /> },
      { name: 'ClockIn Activity', path: 'clocking-activity', icon: <ClockRegular /> },
    ],
  },
  {
    title: 'Client Management',
    items: [
      { name: 'Onboard Client', path: 'onboard-client', icon: <PersonAddRegular /> },
      { name: 'Manage Clients', path: 'clients', icon: <BuildingRegular /> },
      { name: 'Client Categories', path: 'client-category', icon: <FolderRegular /> },
    ],
  },
  {
    title: 'Billing',
    items: [
      { name: 'Generate Client Bill', path: 'billing/generate-bill', icon: <ReceiptRegular /> },
      { name: 'Create Invoice', path: 'billing/create-invoice', icon: <DocumentRegular /> },
      { name: 'Debt Recovery', path: 'billing/debt-recovery', icon: <MoneyRegular /> },
    ],
  },
  {
    title: 'Internal Memo',
    items: [
      { name: 'Inbox', path: 'memo/inbox', icon: <MailRegular /> },
      { name: 'Sent', path: 'memo/sent', icon: <MailRegular /> },
      { name: 'Drafts', path: 'memo/drafts', icon: <MailRegular /> },
    ],
  },
  {
    title: 'Security Operations',
    items: [
      { name: 'Site Monitoring', path: 'site-monitoring', icon: <EyeTrackingRegular /> },
      { name: 'Patrols', path: 'security-operations/patrols', icon: <PersonWalkingRegular /> },
      {
        name: 'Site Attendance',
        path: 'security-operations/site-attendance',
        icon: <PersonAvailableRegular />,
      },
      { name: 'Manage Services', path: 'services', icon: <AppsRegular /> },
      { name: 'Client Services', path: 'client-services', icon: <ShieldRegular /> },
      { name: 'Manage Sites', path: 'sites', icon: <LocationRegular /> },
      { name: 'Manage Site Types', path: 'site-types', icon: <BuildingRegular /> },
      { name: 'Manage Zones', path: 'zones', icon: <LocationRegular /> },
      { name: 'Checkpoints', path: 'checkpoints', icon: <LocationRegular /> },
      { name: 'QR Assets', path: 'qr-assets', icon: <QrCodeRegular /> },
      { name: 'Checkpoint Scans', path: 'checkpoint-scans', icon: <LocationRegular /> },
      { name: 'Incident Reports', path: 'incident-reports', icon: <LocationRegular /> },
      {
        name: 'Guard Operations',
        icon: <ShieldRegular />,
        subItems: [
          { name: 'Guard Lookup', path: 'guard-operations/guard-lookup' },
          { name: 'Assign Guard', path: 'guard-operations/assign-guard' },
          { name: 'Manage Shift', path: 'guard-operations/shifts' },
          { name: 'Assign Supervisor', path: 'guard-operations/assign-supervisor' },
        ],
      },
    ],
  },
  {
    title: 'Security Reporting',
    items: [
      { name: 'Report 1', path: 'reports/report-1', icon: <DocumentBulletListRegular /> },
      { name: 'Report 2', path: 'reports/report-2', icon: <DocumentBulletListRegular /> },
      { name: 'Report 3', path: 'reports/report-3', icon: <DocumentBulletListRegular /> },
      { name: 'Report 4', path: 'reports/report-4', icon: <DocumentBulletListRegular /> },
    ],
  },
  {
    title: 'Admin',
    items: [
      { name: 'Company Setup', path: 'company-setup', icon: <BuildingRegular /> },
      {
        name: 'Manage Users',
        icon: <PersonSettingsRegular />,
        subItems: [
          { name: 'Admin Users', path: 'admin-users' },
          { name: 'Initialize Mailbox', path: 'initialize-mailbox' },
        ],
      },
      { name: 'Login Activity Log', path: 'login-activity', icon: <ClockRegular /> },
      {
        name: 'User Privileges',
        path: 'user-privileges-management',
        icon: <KeyRegular />,
      },
      {
        name: 'Role Privileges',
        path: 'role-privileges-management',
        icon: <ShieldCheckmarkRegular />,
      },
    ],
  },
];

export const NavigationSidebar: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [collapsedSubmenus, setCollapsedSubmenus] = useState<Record<string, boolean>>({});

  const currentPath = location.pathname.split('/account/')[1] || '';

  const toggleGroup = (groupTitle: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  const toggleSubmenu = (itemName: string) => {
    setCollapsedSubmenus(prev => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  // Memoize isActive function to prevent recreation on every render
  // This ensures stable reference in useEffect dependencies
  const isActive = useCallback(
    (path?: string) => {
      if (path === undefined) {
        return false;
      }
      // Handle dashboard routes (both '' and 'dashboard/general' point to same page)
      if (path === '' || path === 'dashboard/general') {
        return currentPath === '' || currentPath === 'dashboard/general';
      }
      return currentPath === path || currentPath.startsWith(`${path}/`);
    },
    [currentPath]
  );

  const handleNavigation = (path?: string) => {
    if (path !== undefined) {
      // Handle empty path (dashboard) - navigate to /account
      if (path === '') {
        navigate('/account');
      } else {
        navigate(`/account/${path}`);
      }
    }
  };

  // Auto-expand groups and submenus containing active path
  // ONLY depend on currentPath - isActive is stable via useCallback
  // This prevents the infinite re-render loop
  useEffect(() => {
    const newCollapsedGroups: Record<string, boolean> = {};
    const newCollapsedSubmenus: Record<string, boolean> = {};

    MENU_GROUPS.forEach(group => {
      const hasActivePath = group.items.some(item => {
        if (item.path && isActive(item.path)) {
          return true;
        }
        if (item.subItems) {
          return item.subItems.some(sub => sub.path && isActive(sub.path));
        }
        return false;
      });

      // Don't collapse if has active path
      if (hasActivePath) {
        newCollapsedGroups[group.title] = false;

        // Also expand submenu if it has active path
        group.items.forEach(item => {
          if (item.subItems) {
            const hasActiveSubPath = item.subItems.some(sub => sub.path && isActive(sub.path));
            if (hasActiveSubPath) {
              newCollapsedSubmenus[item.name] = false;
            }
          }
        });
      }
    });

    setCollapsedGroups(newCollapsedGroups);
    setCollapsedSubmenus(newCollapsedSubmenus);
  }, [currentPath, isActive]); // Only currentPath changes trigger this - isActive is stable

  return (
    <nav className={styles.sidebar} aria-label="Main navigation" role="navigation">
      {MENU_GROUPS.map((group, groupIndex) => (
        <div key={group.title} className={styles.navGroup}>
          {/* Group Label */}
          <button
            className={styles.navGroupLabel}
            onClick={() => toggleGroup(group.title)}
            aria-expanded={!collapsedGroups[group.title]}
            aria-controls={`nav-group-${group.title.replace(/\s+/g, '-').toLowerCase()}`}
            type="button"
          >
            <span>{group.title}</span>
            {collapsedGroups[group.title] ? <ChevronRightRegular /> : <ChevronDownRegular />}
          </button>

          {/* Group Items */}
          {!collapsedGroups[group.title] && (
            <div
              id={`nav-group-${group.title.replace(/\s+/g, '-').toLowerCase()}`}
              role="group"
              aria-label={group.title}
            >
              {group.items.map(item => (
                <div key={item.name}>
                  {/* Regular Item or Submenu Parent */}
                  {item.subItems ? (
                    <>
                      {/* Submenu Parent */}
                      <button
                        className={styles.navItem}
                        onClick={() => toggleSubmenu(item.name)}
                        aria-expanded={!collapsedSubmenus[item.name]}
                        aria-controls={`submenu-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                        type="button"
                      >
                        {item.icon && (
                          <span className={styles.icon} aria-hidden="true">
                            {item.icon}
                          </span>
                        )}
                        <span>{item.name}</span>
                        <span className={styles.chevron} aria-hidden="true">
                          {collapsedSubmenus[item.name] ? (
                            <ChevronRightRegular />
                          ) : (
                            <ChevronDownRegular />
                          )}
                        </span>
                      </button>

                      {/* Submenu Items */}
                      {!collapsedSubmenus[item.name] && (
                        <div
                          id={`submenu-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                          role="group"
                          aria-label={item.name}
                        >
                          {item.subItems.map(subItem => (
                            <button
                              key={subItem.path}
                              className={`${styles.navItem} ${styles.navSubItem} ${
                                isActive(subItem.path) ? styles.navItemActive : ''
                              }`}
                              onClick={() => handleNavigation(subItem.path)}
                              aria-current={isActive(subItem.path) ? 'page' : undefined}
                              type="button"
                            >
                              <span>{subItem.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Regular Item */
                    <button
                      className={`${styles.navItem} ${isActive(item.path) ? styles.navItemActive : ''}`}
                      onClick={() => handleNavigation(item.path)}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      type="button"
                    >
                      {item.icon && (
                        <span className={styles.icon} aria-hidden="true">
                          {item.icon}
                        </span>
                      )}
                      <span>{item.name}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Divider between groups */}
          {groupIndex < MENU_GROUPS.length - 1 && <Divider className={styles.divider} />}
        </div>
      ))}
    </nav>
  );
};
